import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { usePermissions } from '@/hooks/use-permissions';
import type { Database } from '@/integrations/supabase/types';

type UserRole = Database['public']['Enums']['user_role'];

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
}

const ProtectedRoute = ({ children, requiredRole = 'public' }: ProtectedRouteProps) => {
  const { user, profile, isLoading } = useAuth();
  const { hasRole } = usePermissions();
  const location = useLocation();
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    console.log('ProtectedRoute evaluation:', {
      path: location.pathname,
      requiredRole,
      isLoading,
      userExists: !!user,
      userEmail: user?.email,
      profileExists: !!profile,
      profileRole: profile?.role,
      hasRequiredRole: profile ? hasRole(requiredRole) : false,
      initialLoadComplete
    });
    
    // Set initial load complete after auth state is determined
    if (!isLoading && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [location.pathname, requiredRole, isLoading, user, profile, hasRole, initialLoadComplete]);

  // During initial page load, show a loading indicator
  // This prevents flashing of auth screens during page refresh
  if (isLoading) {
    console.log('Auth still loading...');
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
          <p className="text-nation-blue">Loading authentication...</p>
        </div>
      </div>
    );
  }

  // If no user is authenticated, redirect to login
  if (!user) {
    console.log('No authenticated user, redirecting to auth page');
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // If user exists but profile doesn't exist yet or is still loading
  if (!profile) {
    console.log('Profile not loaded yet, showing loading state');
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-purple mx-auto mb-4"></div>
          <p className="text-nation-purple">Loading profile...</p>
        </div>
      </div>
    );
  }
  
  // Check if user has the required role
  if (!hasRole(requiredRole)) {
    console.log('Access denied, user lacks required role. User:', user.email, 'Required role:', requiredRole, 'Current role:', profile?.role);
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If authenticated and has the required role, render the children
  console.log('Access granted to protected route for user:', user.email);
  return <>{children}</>;
};

export default ProtectedRoute;
